/**
 * Übersicht (Dashboard) — Redesign „Runde 23": beantwortet „was ist heute zu tun" statt leerer
 * Nullen. Kopf mit Entscheidungs-Zusammenfassung, kompakter Kennzahlen-Streifen mit Kontextzeilen,
 * „Braucht deine Entscheidung" als Held (echte Auto-Tasks mit Aktion), „Nächste Tage" und der
 * Anfragen-Funnel. Der Assistenten-Werbeblock entfällt — er lebt jetzt in der Chat-Spalte rechts.
 * Alle Zahlen aus echten API-Daten.
 */
import { zahlungsstandMeta } from "../lib/zahlungsstand";
import { Link, useNavigate } from "react-router-dom";
import { useUser } from "@stackframe/react";
import { ANFRAGEN_PFAD, BUCHUNGEN_PFAD, uebersetzeAltesZiel, vorgangPfad } from "../lib/pfade";
import { zeileAusAuto } from "../lib/aufgaben-liste";
import { AufgabenZeileAnsicht } from "../ui/AufgabenZeile";
import { berlinTag, type Auswertung } from "@maestro/auswertung";
import { useApi } from "../hooks/useApi";
import type { EventRow, Inquiry, Payment, TasksBoard } from "../ui/types";
import { anlassAnzeige, EVENT_STATUS, money } from "../ui/format";
import { Card, EmptyState, ErrorNote, Icon, LoadingRows, PageHeader, StatusPill, ButtonRouterLink } from "../ui/components";

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** Kompakte Kennzahl im Kopf-Streifen: Label, großer Wert, Kontextzeile. Klick führt zur Liste. */
function Kpi({ label, value, context, tone, to }: { label: string; value: string | number; context?: string; tone?: "warn" | "down" | "neutral"; to: string }) {
  const ctxClass =
    tone === "down"
      ? "text-[#B23B32] dark:text-[#e39b92]"
      : tone === "warn"
        ? "text-[#6F675A] dark:text-[#b8ae9e]"
        : "text-[#6F675A] dark:text-secondary-fixed-dim";
  return (
    <Card
      to={to}
      as={Link} className="flex flex-col gap-1 px-4 py-3.5 transition-shadow hover:shadow-[0_10px_22px_-14px_rgba(45,38,26,0.28)] dark:hover:shadow-[0_10px_22px_-14px_rgba(0,0,0,0.7)]"
    >
      <span className="text-[10.5px] font-semibold uppercase tracking-wider text-[#9A9080] dark:text-secondary-fixed-dim">{label}</span>
      <span className="text-[26px] font-bold leading-none text-on-background dark:text-inverse-on-surface">{value}</span>
      <span className={`min-h-[15px] text-[11.5px] font-medium ${ctxClass}`}>{context ?? ""}</span>
    </Card>
  );
}

export default function DashboardPage() {
  const user = useUser();
  const navigate = useNavigate();
  const firstName = user?.displayName?.trim().split(/\s+/)[0] ?? "";

  const inquiriesQ = useApi<Inquiry[]>("/api/inquiries");
  const eventsQ = useApi<EventRow[]>("/api/events");
  // „Braucht deine Entscheidung": dieselbe berechnete Auto-Task-Liste wie die Aufgaben-Seite (eine Quelle der Wahrheit).
  const boardQ = useApi<TasksBoard>("/api/tasks/board");
  // Kostenuebernahme-Sichtbarkeit (Domenico/STORIA-Feedback, 07.09.2026): dieselbe dauerhafte
  // Kennzeichnung wie in events.tsx (~Zeile 75-79), dieselbe Datenquelle.
  const signiertStatusQ = useApi<{ eventId: string; signedAt: string }[]>("/api/cost-acceptances/signiert-status");
  const kostenuebernahmeSigniert = new Set((signiertStatusQ.data ?? []).map((r) => r.eventId));

  const inquiries = inquiriesQ.data ?? [];
  const events = eventsQ.data ?? [];
  const loading = inquiriesQ.loading || eventsQ.loading;

  /*
   * Überfällige Zahlungen (für „davon X überfällig") — Punkt 70 a, 21.09.2026.
   *
   * VORHER stand hier eine Schleife: eine HTTP-Anfrage JE VORGANG (`GET /api/events/:id/payments`),
   * alle parallel abgefeuert. Im Messlauf vom 20.09.2026 waren das 336 Anfragen in EINEM Testlauf,
   * und auf dem großen Mandanten brach die Kachel mit „Failed to fetch" ab. Es wuchs linear mit
   * dem Bestand und wäre irgendwann ohne Vorwarnung gekippt.
   *
   * JETZT ein einziger Abruf, serverseitig gefiltert. Der Server schneidet zugleich die Zahlungen
   * nicht gewählter Angebotsfassungen weg (Punkt 67) — die zählte die alte Schleife fälschlich
   * als überfällig mit, weil sie diese Regel nicht kannte.
   *
   * `useApi` statt eines eigenen Effekts: damit hängen Lade- und Fehlerzustand an derselben
   * Mechanik wie die übrigen Abrufe dieser Seite, und die Liste lädt nicht mehr neu, nur weil
   * sich die Reihenfolge der Vorgänge geändert hat.
   */
  const heuteBerlin = berlinTag(new Date());
  const umsatzMonatQ = useApi<Auswertung>(`/api/auswertung?von=${heuteBerlin.slice(0, 7)}-01&bis=${heuteBerlin}`);
  const overdueQ = useApi<(Payment & { eventId: string; eventLabel: string })[]>("/api/payments?ueberfaellig=1");
  const overduePayments = overdueQ.data ?? [];
  const overdueError = overdueQ.error;

  const now = new Date();
  const in30 = new Date(now.getTime() + 30 * 86_400_000);
  const in3days = new Date(now.getTime() + 3 * 86_400_000);

  const openInquiries = inquiries.filter((i) => i.status === "new").length;
  const staleInquiries = inquiries.filter((i) => i.status === "new" && Date.now() - new Date(i.createdAt).getTime() > 48 * 3_600_000);
  const pendingOffers = events.filter((e) => e.status === "offer_sent");
  const expiringOffers = pendingOffers.filter((e) => e.offerValidUntil && new Date(e.offerValidUntil) <= in3days);

  const next30 = events
    .filter((e) => e.eventDate && new Date(e.eventDate) >= startOfMonth(now) && new Date(e.eventDate) <= in30 && !["cancelled", "completed"].includes(e.status))
    .sort((a, b) => (a.eventDate! < b.eventDate! ? -1 : 1));
  const nearest = next30[0];
  const nearestDays = nearest?.eventDate ? Math.max(0, Math.ceil((new Date(nearest.eventDate).getTime() - now.getTime()) / 86_400_000)) : null;
  const nearestLabel = nearestDays == null ? undefined : nearestDays === 0 ? "nächstes heute" : nearestDays === 1 ? "nächstes morgen" : `nächstes in ${nearestDays} Tagen`;

  // R2.9-Folge (31.08.2026): Checkout-Vorgaenge (source==='checkout') erreichen nie
  // status='paid'/'completed' (Migration 135/343, bewusst) — vollstaendig bezahlte werden hier
  // aus den "offenen" Betraegen entfernt. `checkoutDeckung` kommt fertig aus GET /api/events
  // (lib/payment-coverage.ts#leseDeckung) — kein neuer Berechnungsweg.
  const istVollGedecktesCheckoutEvent = (e: EventRow) => e.source === "checkout" && Boolean(e.checkoutDeckung?.gedeckt);

  // Punkt 124 (Bugfix): „Umsatz <Monat>“ kommt aus DERSELBEN Rechnung wie die Seite „Auswertung“
  // (GET /api/auswertung, lib/kennzahlen.ts): zugesagt am Annahmetag, alle Wege, ohne storniert,
  // Vergleich mit denselben Tagen des Vormonats. Vorher rechnete diese Kachel selbst — über das
  // Veranstaltungsdatum ab dem 1. OHNE Enddatum und zeigte so z. B. einen Oktober-Termin als
  // September-Umsatz (Befund 26.09.2026: 731 € statt 99.799 € zugesagt).
  const monthRevenueCents = umsatzMonatQ.data?.aktuell?.zugesagtCents ?? 0;
  const prevMonthCents = umsatzMonatQ.data?.vorher?.zugesagtCents ?? 0;
  const prevMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const momPct = prevMonthCents > 0 ? Math.round(((monthRevenueCents - prevMonthCents) / prevMonthCents) * 100) : null;
  const monthLabel = now.toLocaleDateString("de-DE", { month: "long" });
  const prevMonthLabel = prevMonthDate.toLocaleDateString("de-DE", { month: "long" });

  // Vollstaendig bezahlte Checkout-Vorgaenge sind KEIN offener Betrag mehr, obwohl ihr Status
  // weiterhin 'offer_chosen' ist — sonst zeigt die Kachel faelschlich Geld als ausstehend an,
  // das laengst eingegangen ist.
  // Abrechnung Stufe 3: mit abgeschlossener Abrechnung zählt ihr offener Rest (vom Server, sql/574) —
  // gleich in welchem Status; eine Schlussrechnung mit Minusbetrag ist dabei nie eine offene Forderung.
  const outstandingEvents = events.filter((e) => (e.abrechnungDeckung ? e.abrechnungDeckung.offenCents > 0 : e.status === "offer_chosen" && !istVollGedecktesCheckoutEvent(e)));
  const outstandingCents = outstandingEvents.reduce((s, e) => s + (e.abrechnungDeckung ? e.abrechnungDeckung.offenCents : (e.amountTotalCents ?? 0)), 0);
  const overdueCents = overduePayments.reduce((s, p) => s + (p.amountCents ?? 0), 0);

  // Ø Tage von Anfrage bis Angebotsversand (Kontext für „Angebote offen").
  const offerLeadDays = events.filter((e) => e.offerSentAt).map((e) => (new Date(e.offerSentAt!).getTime() - new Date(e.createdAt).getTime()) / 86_400_000);
  const avgOfferLeadDays = offerLeadDays.length > 0 ? offerLeadDays.reduce((a, b) => a + b, 0) / offerLeadDays.length : null;

  // Funnel (Letzte 90 Tage-Näherung über die vorhandenen Bestände).
  const wonCount = events.filter((e) => ["offer_chosen", "paid", "completed"].includes(e.status)).length;
  const newInquiries = inquiries.filter((i) => i.status === "new").length;
  const offersCreated = events.filter((e) => ["offer_sent", "offer_chosen", "paid", "completed", "offer_declined", "no_response"].includes(e.status)).length;
  const funnelMax = Math.max(1, newInquiries, offersCreated, wonCount);
  const closeRate = newInquiries > 0 ? Math.round((wonCount / newInquiries) * 100) : null;
  const funnel = [
    { label: "Neue Anfragen", n: newInquiries, tone: "low" as const, to: ANFRAGEN_PFAD },
    // F2 (docs/PLAN-ANFRAGE-VORGANG-EINE-IDENTITAET.md): "Angebote erstellt" zaehlt bewusst
    // sowohl vor- (offer_sent/no_response/offer_declined) als auch nach-Zusage-Status
    // (offer_chosen/paid/completed) — seit die Buchungen-Liste nur noch AB Zusage zeigt (D2),
    // gibt es dafuer keine einzelne Zielliste mehr; die Anfragen-Liste ist die naehere Teilmenge
    // (die meisten dieser Faelle stehen noch vor der Zusage).
    { label: "Angebote erstellt", n: offersCreated, tone: "mid" as const, to: ANFRAGEN_PFAD },
    { label: "Gewonnen", n: wonCount, tone: "win" as const, to: `${BUCHUNGEN_PFAD}?filter=won` },
  ];

  // „Braucht deine Entscheidung" — max. 4 je Quelle, insgesamt 6 Zeilen (wie zuvor).
  const todoLoading = loading || boardQ.loading;
  const auto = boardQ.data?.auto ?? [];
  const autoByKind = (kind: (typeof auto)[number]["kind"]) => auto.filter((a) => a.kind === kind);
  // Z3.4 zuerst: ein Posten, bei dem BEIDE Mahnstufen durch sind, ist der einzige in dieser
  // Liste, an dem sonst niemand mehr arbeitet — das System schlaegt dort nichts mehr vor.
  // Ohne diese Zeile waere er beim Wechsel der Auto-Task-Art aus dem Dashboard verschwunden,
  // ausgerechnet im dringendsten Zustand.
  const decisions = [
    ...autoByKind("payment_dunning_exhausted").slice(0, 4),
    ...autoByKind("payment_overdue").slice(0, 4),
    ...autoByKind("inquiry_stale").slice(0, 4),
    // E1 (Aufgaben mit Kontext): ein wartendes Angebot ist keine Entscheidung — der Kunde ist dran.
    // Erst wenn der Betrieb handeln muss (Event naht, abgelaufen, zweimal nachgefasst), erscheint es.
    ...autoByKind("offer_action_needed").slice(0, 4),
  ]
    .slice(0, 6)
    // Stufe 3: dieselbe Zeile wie die Aufgaben-Seite (ui/AufgabenZeile.tsx) — Kontext, Grund-Satz
    // und die eine Aktion kommen aus derselben Umwandlung, nicht aus einer zweiten Dashboard-Fassung.
    .map(zeileAusAuto);


  const decisionCount = decisions.length;
  const decisionLine = todoLoading
    ? "Ich schaue nach, was heute ansteht …"
    : decisionCount > 0
      ? `${decisionCount} ${decisionCount === 1 ? "Sache braucht" : "Sachen brauchen"} heute deine Entscheidung — der Rest läuft.`
      : "Nichts Dringendes — alles läuft.";
  const syncTime = now.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });

  return (
    <>
      <PageHeader
        title="Übersicht"
        actions={
          <ButtonRouterLink to="/anfragen?neu=1" variant="primary">
            <Icon name="add" className="text-[18px]" /> Neue Anfrage
          </ButtonRouterLink>
        }
      />

      {/* Kopfzeile: großes Datum + Entscheidungs-Zusammenfassung, rechts der Sync-Status. */}
      <div className="mb-5 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-serif text-[26px] font-semibold leading-tight text-on-background dark:text-inverse-on-surface">
            {now.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" })}
          </h2>
          <p className="mt-1 text-[13.5px] text-[#6F675A] dark:text-secondary-fixed-dim">
            {firstName ? `${firstName}, ` : ""}
            {decisionLine}
          </p>
        </div>
        <span className="inline-flex items-center gap-2 text-[12px] font-medium text-[#9A9080] dark:text-secondary-fixed-dim">
          <span className="h-2 w-2 rounded-full bg-[#6F675A]" aria-hidden /> Alles synchronisiert · {syncTime}
        </span>
      </div>

      {overdueError && (
        <div className="mb-4">
          <ErrorNote message={`Überfällige Zahlungen konnten nicht vollständig geladen werden: ${overdueError}`} />
        </div>
      )}

      {/* Kennzahlen-Streifen (5 kompakte Kacheln mit Kontextzeile). */}
      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        <Kpi
          label="Offene Anfragen"
          value={openInquiries}
          to="/anfragen"
          context={staleInquiries.length > 0 ? `${staleInquiries.length} länger als 48 Std.` : undefined}
          tone={staleInquiries.length > 0 ? "warn" : "neutral"}
        />
        <Kpi
          label="Angebote offen"
          value={pendingOffers.length}
          // F2: "Angebote offen" (status offer_sent, vor Zusage) gehoert seit D2 zur
          // Anfragen-Liste — die Buchungen-Liste zeigt nur noch Faelle AB der Zusage.
          to={ANFRAGEN_PFAD}
          context={
            expiringOffers.length > 0
              ? `${expiringOffers.length} ${expiringOffers.length === 1 ? "endet" : "enden"} bald`
              : avgOfferLeadDays != null
                ? `Ø ${avgOfferLeadDays < 1 ? "< 1" : avgOfferLeadDays.toFixed(1).replace(".", ",")} Tage bis Antwort`
                : undefined
          }
          tone={expiringOffers.length > 0 ? "warn" : "neutral"}
        />
        <Kpi label="Events · 30 Tage" value={next30.length} to="/kalender" context={nearestLabel} tone="neutral" />
        <Kpi
          label={`Umsatz ${monthLabel}`}
          value={money(monthRevenueCents)}
          to="/auswertung?zr=monat"
          context={momPct != null ? `${momPct >= 0 ? "+" : ""}${momPct}% ggü. ${prevMonthLabel} (gleiche Tage) · zugesagt` : "zugesagt, nach Annahmetag"}
          tone={momPct != null && momPct < 0 ? "down" : "neutral"}
        />
        <Kpi
          label="Offene Beträge"
          value={money(outstandingCents)}
          to="/zahlungen?filter=open"
          context={overdueCents > 0 ? `davon ${money(overdueCents)} überfällig` : outstandingEvents.length > 0 ? `${outstandingEvents.length} offen` : undefined}
          tone={overdueCents > 0 ? "down" : "neutral"}
        />
      </div>

      {/* Links: „Braucht deine Entscheidung" (Held) + Funnel · Rechts: „Nächste Tage". */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
        <div className="flex flex-col gap-4">
          <Card className="flex flex-col overflow-hidden">
            <div className="flex items-center gap-2 border-b border-[#F2ECDF] px-[18px] py-3.5 dark:border-white/[0.06]">
              <span className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">Braucht deine Entscheidung</span>
              {decisionCount > 0 && (
                <span className="rounded-full bg-[#F4EEE1] px-[7px] py-0.5 text-[10.5px] font-semibold text-[#6F675A] dark:bg-white/[0.08] dark:text-secondary-fixed-dim">
                  {decisionCount}
                </span>
              )}
              <Link to="/aufgaben" className="ml-auto text-[12px] font-medium text-primary hover:underline">
                Alle ansehen
              </Link>
            </div>
            {todoLoading && <LoadingRows rows={3} />}
            {!todoLoading && decisions.length === 0 && (
              <EmptyState icon="task_alt" title="Alles im Griff" hint="Nichts, was heute deine Entscheidung braucht." />
            )}
            {!todoLoading && decisions.length > 0 && (
              <ul>
                {decisions.map((z) => (
                  <AufgabenZeileAnsicht key={z.key} z={z} heute={heuteBerlin} onAktion={(ziel) => navigate(uebersetzeAltesZiel(ziel))} />
                ))}
              </ul>
            )}
          </Card>

          {/* Anfragen-Funnel */}
          <Card className="flex flex-col overflow-hidden">
            <div className="flex items-center gap-2 border-b border-[#F2ECDF] px-[18px] py-3.5 dark:border-white/[0.06]">
              <span className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">Anfragen-Funnel</span>
              <span className="ml-auto text-[11.5px] text-[#9A9080] dark:text-secondary-fixed-dim">Letzte 90 Tage</span>
            </div>
            <div className="flex flex-col gap-3 p-[18px]">
              {funnel.map((f) => (
                <div key={f.label} className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between text-[12px]">
                    <span className="font-medium text-[#6F675A] dark:text-secondary-fixed-dim">{f.label}</span>
                    <span className="font-semibold text-on-background dark:text-inverse-on-surface">
                      {f.n}
                      {f.tone === "win" && closeRate != null ? ` · ${closeRate} %` : ""}
                    </span>
                  </div>
                  <Link
                    to={f.to}
                    title={`${f.label} ansehen`}
                    className={`h-2.5 rounded-full transition-opacity hover:opacity-80 ${
                      f.tone === "low"
                        ? "bg-[#31619B] dark:bg-[#4f7fc0]" /* Diagramm-Balken: Ruhige-Hinweise-Ausnahme */
                        : f.tone === "mid"
                          ? "bg-[#C98A1B] dark:bg-[#d9a23f]"
                          : "bg-[#2E9963]"
                    }`}
                    style={{ width: `${Math.max(6, Math.round((f.n / funnelMax) * 100))}%` }}
                  />
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Nächste Tage */}
        <Card className="flex h-fit flex-col overflow-hidden">
          <div className="flex items-center gap-2 border-b border-[#F2ECDF] px-[18px] py-3.5 dark:border-white/[0.06]">
            <span className="text-[14.5px] font-semibold text-on-background dark:text-inverse-on-surface">Nächste Tage</span>
            <Link to="/kalender" className="ml-auto text-[12px] font-medium text-primary hover:underline">
              Kalender
            </Link>
          </div>
          {loading ? (
            <LoadingRows rows={3} />
          ) : next30.length === 0 ? (
            <EmptyState icon="event_upcoming" title="Keine Termine in Sicht" hint="Bestätigte Termine der nächsten 30 Tage erscheinen hier." />
          ) : (
            next30.slice(0, 6).map((e) => {
              const confirmed = ["offer_chosen", "paid", "completed"].includes(e.status);
              const d = e.eventDate ? new Date(e.eventDate) : null;
              return (
                <Link
                  key={e.id}
                  to={vorgangPfad(e.id)}
                  className="flex items-center gap-3.5 border-b border-[#F6F0E4] px-[18px] py-3 transition-colors last:border-b-0 hover:bg-[#FBF6EC] dark:border-white/[0.05] dark:hover:bg-white/[0.03]"
                >
                  <div className={`w-[42px] shrink-0 rounded-[9px] py-1 text-center ${confirmed ? "bg-tertiary-container" : "bg-[#F0EAE0]"}`}>
                    <div className={`font-bold leading-[1.1] ${"text-[#6F675A]"}`} style={{ fontSize: "15px" }}>
                      {d ? d.getDate() : "–"}
                    </div>
                    <div className={`text-[9.5px] font-semibold tracking-wide ${"text-[#9A9080]"}`}>
                      {d ? d.toLocaleDateString("de-DE", { month: "short" }).toUpperCase().replace(".", "") : ""}
                    </div>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold text-on-background dark:text-inverse-on-surface">{anlassAnzeige(e.occasion) ?? "Event"}</p>
                    <p className="truncate text-[11.5px] text-[#6F675A] dark:text-secondary-fixed-dim">
                      {e.guestCount ? `${e.guestCount} Gäste` : ""}
                      {e.guestCount && e.location ? " · " : ""}
                      {e.location ?? ""}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                    <StatusPill meta={EVENT_STATUS[e.status]} />
                    {zahlungsstandMeta(e) && <StatusPill meta={zahlungsstandMeta(e)!} testId={zahlungsstandMeta(e)!.tone === "warn" ? "rest-offen" : "zahlungsstand-abzeichen"} />}
                    {kostenuebernahmeSigniert.has(e.id) && (
                      <StatusPill meta={{ label: "🧾 Kostenübernahme signiert", tone: "success" }} />
                    )}
                  </div>
                </Link>
              );
            })
          )}
        </Card>
      </div>
    </>
  );
}
